<template>
  <div v-if="isVisible" v-style="{ bg: backgroundColor, py: paddingsMapping }">
    <div :class="[insetClasses, withMediaClasses]">
      <div v-if="hasMedia" :class="[mediaSize[imageWidth!], roundedCornersClasses]">
        <base-picture
          v-if="responsiveMedia.images"
          v-bind="responsiveMedia.images"
          class="full"
          :fetchpriority
          :lazy
        />
        <vf-video
          v-else-if="responsiveMedia.video"
          v-bind="responsiveMedia.video"
          ref="videoRef"
          :autoplay="!lazy"
          class="full"
          loop
          :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
        />
        <cms-shared-hot-zones v-if="hasHotZones" :media="media!" />
      </div>
      <div class="grow">
        <h2
          v-if="hasTitle"
          v-style:c="titleColor"
          class="mb-4"
          :class="[titleStyle, { 'px-4': !style }]"
        >
          <span v-if="renderedTitleHtml" v-html="renderedTitleHtml" />
          <span v-else>{{ title }}</span>
        </h2>
        <div v-style:b="borderColor" :class="bottomDivider && dividerClass">
          <vf-accordion
            v-for="(accordion, i) in accordions"
            :key="i"
            v-style:b="borderColor"
            :icon="iconsStyle"
            :icon-collapse="iconsMap[iconsStyle]"
            :open="accordion.open"
            :title="accordion.title"
            :title-color
            :title-icon="accordion.icon"
            :variant="style"
            @close="onAccordionInteraction(accordion.title, false)"
            @expand="onAccordionInteraction(accordion.title, true)"
          >
            <div class="pb-3 pb-6 body-3">
              <lazy-cms-rich-text :content="{ richText: accordion.richText, componentMaxWidth: maxWidth, alignment }" />
            </div>
          </vf-accordion>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { AccordionsContent } from '#types/components/cms/accordions'
import type { ResponsiveColor, SplitMediaWidth } from '#types/content'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{ content: AccordionsContent }>()
const { $feature, $gtm } = useNuxtApp()
const route = useRoute()

const {
  accordions,
  alignment,
  backgroundColor,
  bottomDivider,
  iconsStyle = 'chevron',
  imagePosition,
  imageWidth,
  isJsonldEnabled = false,
  maxWidth,
  media,
  paddings,
  showWhenFiltersPresent,
  style,
  title,
  titleColor,
  titleHtml,
  titleStyle,
} = content

const isVisible = computed(() => !route.query.filters || showWhenFiltersPresent)
const renderedTitleHtml = computed(() => $feature.showTitleHtml && hasTextContent(titleHtml) ? titleHtml : '')
const hasTitle = computed(() => !!renderedTitleHtml.value || hasTextContent(title))

const { ds: { breakpoints }, components: { cms } } = useAppConfig()
const { accordion: { defaultBorderColor, defaultTitleColor, dividerClass }, section } = cms
const insetClasses = getContentInset(content, section)
const { componentSpacingMap, componentCorners } = section

const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)

const borderColor = Object.keys(breakpoints).reduce((acc, cur) => {
  const valueForBreakpoint = getValueForBreakpoint(cur as keyof ResponsiveColor, titleColor)
  if (valueForBreakpoint?.toLowerCase() !== defaultTitleColor) acc[cur] = valueForBreakpoint
  else acc[cur] = defaultBorderColor
  return acc
}, {} as ResponsiveColor)

const iconsMap = {
  plus: 'minus',
  chevron: ''
}

const paddingsMapping = Object.keys(breakpoints).reduce((acc, bp) => ({
  ...acc,
  [bp]: componentSpacingMap[paddings?.[bp]]
}), {})

const roundedCornersClasses = getRoundedCorners(content, componentCorners)
const responsiveMedia = getResponsiveMedia(media)
const hasMedia = responsiveMedia.images || responsiveMedia.video
const hasHotZones = Object.values(media || []).some((m) => m.enableImageMap)
const videoRef = useTemplateRef('videoRef')

const mediaSize: Record<SplitMediaWidth, string> = {
  60: 'md:w-3/5',
  50: 'md:w-1/2',
  40: 'md:w-2/5'
}

const withMediaClasses = hasMedia
  ? [
      'f-col gap-4 md:items-start',
      imagePosition === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'
    ]
  : []

useSchemaOrg(() => {
  if (!isJsonldEnabled || !isVisible.value) return []

  const faqAccordionSchema = getJsonLdForFaqPage(
    accordions.map((a) => ({
      title: a.title,
      richText: stripHtml(isString(a.richText) ? a.richText : a.richText.html)
    }))
  )
  return faqAccordionSchema ? [faqAccordionSchema] : []
})

const onAccordionInteraction = createAccordionInteractionHandler($gtm)

useAutoplay(videoRef)
</script>
